import { useState } from 'react'
import * as ReactColor from 'react-color'
const { ChromePicker } = ReactColor
type ColorResult = ReactColor.ColorResult
import { Popover, PopoverContent, PopoverTrigger } from '@/components/ui/popover'
import { Button } from '@/components/ui/button'
import { Input } from '@/components/ui/input'
import { Label } from '@/components/ui/label'
import { Badge } from '@/components/ui/badge'
import { AlertCircle, Check } from 'lucide-react'
import {
  PRESET_COLORS,
  getAccessibilityLevel,
  getTextColorForBackground
} from '@/utils/colorAccessibility'

interface CategoryColorPickerProps {
  color: string
  onChange: (color: string) => void
  label?: string
  disabled?: boolean
}

export const CategoryColorPicker: React.FC<CategoryColorPickerProps> = ({
  color,
  onChange,
  label = 'Color',
  disabled = false,
}) => {
  const [isOpen, setIsOpen] = useState(false)
  const [tempColor, setTempColor] = useState(color)

  const handleColorChange = (colorResult: ColorResult) => {
    setTempColor(colorResult.hex)
  }

  const handleApply = () => {
    onChange(tempColor)
    setIsOpen(false)
  }

  const handlePresetClick = (presetColor: string) => {
    onChange(presetColor)
    setTempColor(presetColor)
  }

  const accessibilityWhite = getAccessibilityLevel(color, '#FFFFFF')
  const accessibilityBlack = getAccessibilityLevel(color, '#000000')
  const textColor = getTextColorForBackground(color)

  return (
    <div className="space-y-3">
      <Label>{label}</Label>

      {/* Color Input and Picker Trigger */}
      <div className="flex gap-2">
        <Popover open={isOpen} onOpenChange={setIsOpen}>
          <PopoverTrigger asChild>
            <Button
              variant="outline"
              className="w-20 h-10 p-1"
              disabled={disabled}
              type="button"
            >
              <div
                className="w-full h-full rounded border border-border"
                style={{ backgroundColor: color }}
              />
            </Button>
          </PopoverTrigger>
          <PopoverContent className="w-auto p-4" align="start">
            <div className="space-y-4">
              {/* Chrome Color Picker */}
              <ChromePicker
                color={tempColor}
                onChange={handleColorChange}
                disableAlpha
              />

              {/* Preset Colors */}
              <div>
                <Label className="text-xs mb-2 block">Preset Colors</Label>
                <div className="grid grid-cols-6 gap-2">
                  {PRESET_COLORS.map((preset) => (
                    <button
                      key={preset.hex}
                      className="w-8 h-8 rounded border-2 border-border hover:border-primary transition-colors relative"
                      style={{ backgroundColor: preset.hex }}
                      onClick={() => handlePresetClick(preset.hex)}
                      title={preset.name}
                      type="button"
                    >
                      {color === preset.hex && (
                        <Check
                          className="absolute inset-0 m-auto h-4 w-4"
                          style={{ color: getTextColorForBackground(preset.hex) }}
                        />
                      )}
                    </button>
                  ))}
                </div>
              </div>

              {/* Action Buttons */}
              <div className="flex justify-end gap-2">
                <Button
                  variant="outline"
                  size="sm"
                  onClick={() => setIsOpen(false)}
                  type="button"
                >
                  Cancel
                </Button>
                <Button
                  size="sm"
                  onClick={handleApply}
                  type="button"
                >
                  Apply
                </Button>
              </div>
            </div>
          </PopoverContent>
        </Popover>

        {/* Hex Input */}
        <Input
          value={color}
          onChange={(e) => {
            const value = e.target.value
            if (/^#[0-9A-Fa-f]{0,6}$/.test(value)) {
              onChange(value)
              setTempColor(value)
            }
          }}
          placeholder="#3B82F6"
          className="flex-1"
          disabled={disabled}
        />
      </div>

      {/* Accessibility Indicators */}
      <div className="space-y-2">
        <div className="flex items-center gap-2 text-xs">
          <span className="text-muted-foreground">Contrast with white:</span>
          <Badge
            variant={accessibilityWhite === 'Fail' ? 'destructive' :
                    accessibilityWhite === 'AAA' ? 'default' : 'secondary'}
            className="text-xs"
          >
            {accessibilityWhite}
          </Badge>
          {accessibilityWhite === 'Fail' && (
            <span className="text-xs text-destructive flex items-center gap-1">
              <AlertCircle className="h-3 w-3" />
              Low contrast
            </span>
          )}
        </div>

        <div className="flex items-center gap-2 text-xs">
          <span className="text-muted-foreground">Contrast with black:</span>
          <Badge
            variant={accessibilityBlack === 'Fail' ? 'destructive' :
                    accessibilityBlack === 'AAA' ? 'default' : 'secondary'}
            className="text-xs"
          >
            {accessibilityBlack}
          </Badge>
          {accessibilityBlack === 'Fail' && (
            <span className="text-xs text-destructive flex items-center gap-1">
              <AlertCircle className="h-3 w-3" />
              Low contrast
            </span>
          )}
        </div>
      </div>

      {/* Preview */}
      <div className="p-3 bg-muted rounded-lg">
        <div className="flex items-center gap-3">
          <div
            className="w-10 h-10 rounded-lg border border-border shadow-sm"
            style={{ backgroundColor: color }}
          />
          <div>
            <div className="font-medium">Category Preview</div>
            <div
              className="text-xs px-2 py-1 rounded-full inline-block mt-1"
              style={{
                backgroundColor: color,
                color: textColor
              }}
            >
              Sample Badge
            </div>
          </div>
        </div>
      </div>

      <p className="text-xs text-muted-foreground">
        Used to visually distinguish categories. Choose colors with good contrast for accessibility.
      </p>
    </div>
  )
}